/**
 * Studio's copy for one M5 surface (RFC 0006), kept beside strings.ts and
 * spread into it: `strings.directory` and `strings.problems`. Plain data —
 * it imports nothing, so strings.ts stays the one place every screen reads.
 *
 * This surface: the organization's groups and vendors (§3.3, §4.1, §4.3).
 */
export const directoryStrings = {
  back: "Projects",
  retry: "Read again",
  memberLabel: (name: string) => name,
  unknownMember: (id: string) => `Member ${id.slice(0, 8)}`,
  everyProject: "Every project",
  unknownProject: (id: string) => `Project ${id.slice(0, 8)}`,
  status: { invited: "Invited", active: "Active" } as Record<string, string>,
  visibility: { all: "Sees what their roles allow", assigned: "Sees assigned work only" } as Record<string, string>,
  // groups
  groups: {
    title: "Groups",
    lead: "A group is a named set of members — “de reviewers”, “legal” — that assignments and approvals can name instead of naming people. Groups carry no permissions: what a member may do still comes from their roles.",
    readOnly: "You can see the groups. Creating and changing them needs the admin or owner role.",
    loading: "Loading groups…",
    failed: "The groups could not be read, so nothing below is known — not even whether there are any.",
    empty: "No groups yet. Create one to give work or approvals to a set of people.",
    membersFailed: "The member list could not be read, so members are shown by id.",
    create: "Create group",
    name: "Group name",
    nameHint: "1 to 100 characters, e.g. “de reviewers”.",
    created: (name: string) => `Created the group ${name}.`,
    members: (n: number) => `${n.toLocaleString()} member${n === 1 ? "" : "s"}`,
    noMembers: "Nobody is in this group yet.",
    add: "Add member",
    addLabel: (group: string) => `Member to add to ${group}`,
    choose: "Choose a member…",
    added: (who: string, group: string) => `Added ${who} to ${group}.`,
    remove: "Remove",
    fromGroup: (who: string) => ` ${who} from the group`,
    removed: (who: string, group: string) => `Removed ${who} from ${group}.`,
    rename: "Rename",
    renameLabel: (group: string) => `New name for ${group}`,
    renamed: (name: string) => `Renamed the group to ${name}.`,
    save: "Save name",
    ofGroup: (group: string) => ` ${group}`,
    delete: "Delete",
    deleteTitle: (group: string) => `Delete the group ${group}?`,
    deleteLead: "Its members stay members. Assignments and approvals that named the group keep naming it and reach nobody.",
    deleteConfirm: "Delete group",
    deleted: (name: string) => `Deleted the group ${name}.`,
  },
  // vendors
  vendors: {
    title: "Vendors",
    lead: "A vendor is an agency or freelancer team working inside this organization: a named set of members, not an organization of its own. Its people are invited here as the vendor's members and see only the work assigned to them.",
    readOnly: "You can see the vendors. Adding and changing them needs the admin or owner role.",
    loading: "Loading vendors…",
    failed: "The vendors could not be read, so nothing below is known — not even whether there are any.",
    empty: "No vendors yet.",
    membersFailed: "The member list could not be read, so member counts are not known.",
    columns: { name: "Vendor", contact: "Contact", locales: "Locales offered", members: "Members" },
    members: (n: number | undefined) => (n === undefined ? "—" : n.toLocaleString()),
    create: "Add vendor",
    name: "Name",
    contact: "Contact (optional)",
    contactHint: "A name or an address. Shown, never mailed to.",
    locales: "Locales offered (optional)",
    localesHint: "Separated by spaces or commas, e.g. “de fr-CA”. They describe the vendor and grant nothing.",
    created: (name: string) => `Added the vendor ${name}.`,
    noLocales: "—",
  },
  vendor: {
    loading: "Loading the vendor…",
    failed: "The vendor could not be read.",
    allVendors: "All vendors",
    details: "Details",
    save: "Save details",
    saved: (name: string) => `Saved ${name}.`,
    readOnly: "You can see this vendor. Changing it needs the admin or owner role.",
    delete: "Delete vendor",
    deleteTitle: (name: string) => `Delete the vendor ${name}?`,
    deleteLead: "Only a vendor nobody works for any more can be deleted. Take its members off it first.",
    deleteConfirm: "Delete vendor",
    deleted: (name: string) => `Deleted the vendor ${name}.`,
    visibilityTitle: "What the vendor's members can see",
    visibilityLead: "Every member of a vendor is a translator with assignment-scoped visibility. That restriction is enforced by the server on every read, not by Studio.",
    canDo: "They can",
    cannotDo: "They can't",
    can: [
      "read the translation units in assignments given to them — open ones, and ones completed within the last 30 days",
      "read what translating those units needs: the source text with its description and arguments, where it appears, the style guide and termbase for the locale, and translation-memory matches for those units",
      "write translations in those units",
    ],
    cannot: [
      "review, approve, publish, export or import anything",
      "search translation memory or the termbase across the organization",
      "list or open any other message, project or locale",
    ],
    members: "Members",
    membersLead: "The vendor's people. Project scope narrows which projects their roles apply to; empty means every project.",
    membersFailed: "The member list could not be read, so the vendor's members are not known.",
    noMembers: "Nobody works for this vendor yet. Invite its people below.",
    columns: { who: "Member", status: "Status", roles: "Roles", locales: "Locales", projects: "Project scope", visibility: "Visibility", actions: "Actions" },
    allLocales: "Every locale",
    projectsFailed: "The project list could not be read, so projects are shown by id.",
    editScope: "Project scope",
    ofMember: (who: string) => ` of ${who}`,
    scopeTitle: (who: string) => `Projects ${who} works on`,
    scopeLead: "Their roles apply only in the projects checked here. Check none for every project.",
    scopeSave: "Save project scope",
    scopeSaved: (who: string) => `Saved ${who}'s project scope.`,
    offVendor: "Take off vendor",
    offVendorTitle: (who: string, vendor: string) => `Take ${who} off ${vendor}?`,
    offVendorLead: "They stay a member of the organization. A vendor's member always reads assigned work only; the server decides what their visibility becomes, and refuses the change if it can't stand on its own.",
    offVendorConfirm: "Take off vendor",
    offVendored: (who: string) => `${who} no longer works for this vendor.`,
    invite: "Invite a member",
    inviteLead: "The person is invited as a translator who sees assigned work only — that is not a choice: every vendor member is restricted this way. They join when they sign in with this address.",
    email: "Email",
    inviteLocales: "Locales (optional)",
    inviteLocalesHint: "The locales they translate, separated by spaces or commas. Empty: every locale.",
    inviteProjects: "Projects (optional)",
    inviteProjectsHint: "Their roles apply only in the projects checked. None: every project.",
    inviteSend: "Send invitation",
    invited: (email: string) => `Invited ${email} as a member of this vendor.`,
    inviteNeeds: "Inviting a vendor's people needs the admin or owner role.",
  },
};

/** Sentences for this surface's problem codes, merged into `strings.problems`. */
export const directoryProblems: Record<string, string> = {
  invalid_group_name: "A group's name is 1 to 100 characters.",
  group_full: "This group is full: it holds as many members as a group may.",
  not_in_group: "That member isn't in this group any more. The list has been read again.",
  individual_tenant: "Groups and vendors belong to an organization. A personal workspace has neither.",
  invalid_vendor_name: "A vendor's name is 1 to 100 characters.",
  invalid_vendor_contact: "The contact is at most 200 characters.",
  vendor_has_members: "This vendor still has members. Take each of them off the vendor, or remove them, before deleting it — that is a decision about each person.",
  already_member: "That address is already a member of this organization.",
  vendor_member_visibility: "A vendor's member always sees assigned work only.",
  assigned_visibility_role: "Only a translator can be limited to assigned work.",
  invalid_visibility: "That visibility isn't valid.",
  scope_exceeds_grant: "You can only give access within the projects you yourself work on.",
  access_and_restriction: "Roles and locales change separately from project scope, vendor and visibility. Change one, then the other.",
  too_many_projects: "A member's project scope names at most 100 projects.",
  last_owner: "The organization's last owner can't be changed.",
  owner_project_scoped: "An owner can't be limited to some projects.",
  owner_change_forbidden: "Only an owner can change an owner.",
  locales_need_locale_role: "Locales apply to translators and reviewers only.",
};
